'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'

// walrus
import Box from '@components/kit/box'
import CheckBoxWrapper from '@components/kit/form/inputs/checkbox-input/checkbox-wrapper'
import Pressable, { PressableProps } from '@components/kit/pressable'
import ScrollView from '@components/kit/scroll-view'
import Text from '@components/kit/text'

// lib
import { ProfileContext } from '../profile'
import MemberInfoPopper from './member-info-popper'
import { useConditionsStore } from '@store/Conditions'
import { IAvailableCondition, IUserCondition } from '@store/Conditions/types'
import { ChatContext } from '../../chat.web'
import Button from '@components/kit/button'
import { HorizontalRule } from '@components/table'

type ConditionItemProps = {
  condition: IAvailableCondition
  isSelected: boolean
  addCondition: (condition: IAvailableCondition) => Promise<boolean>
  removeCondition: (condition: IAvailableCondition) => Promise<boolean>
  refetchUserConditions: () => void
}

const ConditionItem = ({
  condition,
  isSelected,
  addCondition,
  removeCondition,
  refetchUserConditions
}: ConditionItemProps) => {
  const handleToggle = () => {
    const toggleCondition = isSelected ? removeCondition : addCondition
    toggleCondition(condition).finally(refetchUserConditions)
  }
  return (
    <React.Fragment>
      <Pressable
        onPress={handleToggle}
        flexDirection='row'
        paddingX='04'
        paddingY='08'
      >
        <CheckBoxWrapper isChecked={isSelected} size={20} children={null} />
        <Text textColor='newPurple' font='display.14.bold' paddingLeft='12'>
          {condition.name}
        </Text>
      </Pressable>
      <HorizontalRule height='1px' />
    </React.Fragment>
  )
}

type MemberConditionsPopoverProps = {
  conditionsAvailable: IAvailableCondition[]
  selectedConditions: IUserCondition[]
  refetchUserConditions: () => void
}

const MemberConditionsPopover = ({
  conditionsAvailable,
  selectedConditions,
  refetchUserConditions
}: MemberConditionsPopoverProps) => {
  const { userId } = React.useContext(ChatContext)
  const { addUserCondition, removeUserCondition } =
    useConditionsStore.getState()
  const addCondition = (condition: IAvailableCondition) =>
    addUserCondition(userId, condition.id)
  const removeCondition = (condition: IAvailableCondition) =>
    removeUserCondition(userId, condition.id)
  const { isConditionsPopoverOpen, openConditionsPopover } =
    React.useContext(ProfileContext)
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null)

  return (
    <>
      <Button
        ref={setAnchor}
        variant='tertiary'
        size='fit'
        onPress={openConditionsPopover}
      >
        Edit Conditions
      </Button>
      <MemberInfoPopper
        open={isConditionsPopoverOpen && !!conditionsAvailable?.length}
        anchorEl={anchor}
      >
        <Box
          flexDirection='column'
          backgroundColor='white'
          borderRadius='large'
          boxShadow='large'
          maxHeight='200px'
          style={{ overflow: 'hidden' }}
        >
          <ScrollView padding='12'>
            {conditionsAvailable.map(condition => {
              const isSelected = selectedConditions?.some(
                selectedCondition =>
                  selectedCondition.condition_id === condition.id
              )
              return (
                <ConditionItem
                  key={condition.id}
                  condition={condition}
                  isSelected={isSelected}
                  addCondition={addCondition}
                  removeCondition={removeCondition}
                  refetchUserConditions={refetchUserConditions}
                />
              )
            })}
          </ScrollView>
        </Box>
      </MemberInfoPopper>
    </>
  )
}

export default MemberConditionsPopover
